import type { ErrorResponse } from '@shared/types/http'

import { endpoint } from '@shared/services/endpoint'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'

import type { Cart } from '../types/cart'

import {
  deleteRequest,
  getRequest,
  putRequest,
  sendRequest,
} from '../../../shared/services/fetcher'

export type AddToCartRequest = {
  id: number
  quantity: number
  variation: {
    attribute: string
    value: string
  }[]
}

export type UpdateCartRequest = {
  quantity: number
}

export type CartResponse = Cart

export const useCart = () => {
  const useAdd = () => {
    const {
      data,
      error,
      isMutating: isLoading,
      trigger,
    } = useSWRMutation<Cart, ErrorResponse, string, AddToCartRequest>(
      endpoint.frontend.CART,
      sendRequest,
    )

    const addItem = async (request: AddToCartRequest) => {
      return await trigger(request)
    }

    return { addItem, data, error, isLoading }
  }

  const useGet = () => {
    return useSWR<Cart, ErrorResponse>(endpoint.frontend.CART, getRequest)
  }

  const useRemove = (key: string) => {
    const {
      data,
      error,
      isMutating: isLoading,
      trigger,
    } = useSWRMutation<Cart, ErrorResponse>(
      `${endpoint.frontend.CART}/${key}`,
      deleteRequest,
    )

    const removeItem = async () => {
      return await trigger()
    }

    return { data, error, isLoading, removeItem }
  }

  const useUpdate = (key: string) => {
    const {
      data,
      error,
      isMutating: isLoading,
      trigger,
    } = useSWRMutation<Cart, ErrorResponse, string, UpdateCartRequest>(
      `${endpoint.frontend.CART}/${key}`,
      putRequest,
    )

    const updateItem = async (request: UpdateCartRequest) => {
      return await trigger(request)
    }

    return { data, error, isLoading, updateItem }
  }

  return { add: useAdd, get: useGet, remove: useRemove, update: useUpdate }
}

export default useCart
